import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import LocalStorage from "../../utils/local-storage";
import Pusher from "pusher-js";
import state from "pusher-js/src/core/http/state";

export const PaymentContext = createContext(null);

export enum PaymentStage {
  init = "init",
  transfer_details = "transfer_details",
  bank_transfer = "bank_transfer",
  expired_bank = "expired_bank",
  validate_transfer = "validate_transfer",
  auth_pin = "auth_pin",
  auth_otp = "auth_otp",
  auth_threeds = "auth_threeds",
  auth_dob = "auth_dob",
  card_enroll = "card_enroll",
  avs = "avs",
  phone_details = "phone_details",
  auth_payattitude = "auth_payattitude",
  expired_payattitude = "expired_payattitude",
  failed = "failed",
  success = "success",
  expired = "expired",
  end = "end",
  mobile_money_details = "mobile_money_details",
  auth_mobilemoney = "auth_mobilemoney",
  expired_mobilemoney = "expired_mobilemoney",
}

export enum PaymentMethod {
  BankTransfer = "bank_transfer",
  Card = "card",
  PayAttitude = "payattitude",
  MobileMoney = "mobile_money",
}

export const InitialStage = {
  [PaymentMethod.Card]: PaymentStage.init,
  [PaymentMethod.BankTransfer]: PaymentStage.transfer_details,
  [PaymentMethod.PayAttitude]: PaymentStage.phone_details,
  [PaymentMethod.MobileMoney]: PaymentStage.mobile_money_details,
};

export enum PaymentStatus {
  failed = "failed",
  success = "success",
  pending = "pending",
  processing = "processing",
  initiated = "initiated",
}

export enum AuthModel {
  NO_AUTH = "NO_AUTH",
  PIN = "PIN",
  OTP = "OTP",
  CARD_ENROLL = "CARD_ENROLL",
  AVS = "AVS",
  "3DS" = "3DS",
  BIRTHDAY = "BIRTHDAY",
  PHONE = "PHONE",
}

export interface Card {
  cardNumber: string;
  cvv: string;
  expiry: string;
  otp?: string;
  pin?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface Bank {
  accountNumber: string;
  bankName: string;
}

export interface PayAttitude {
  phoneNumber: string;
}
export interface MobileMoney {
  mobileNumber: string;
  operator: string;
}

export interface Address {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface Charge {
  id: number;
  authorization: {
    mode: AuthModel;
    redirect?: string;
    withCallback?: boolean;
    metadata?: {
      canAuthorize: boolean;
      formUrl?: string;
      formData?: Record<string, string>;
      html?: string;
    };
  };
  auth_model: AuthModel;
  amount: number;
  currency: string;
  fee: number;
  message: string;
  status: PaymentStatus;
  reference: string;
  description: string;
  type: PaymentMethod;
  customer: any;
  settlementDestination: string;
  virtualAccount?: {
    bankName?: string;
    bankCode: string;
    accountName: string;
    accountNumber: string;
    expiresAt: Date;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface RawCharge extends Charge {
  callbackUrl: string;
  channelReference: string;
  channelMessage: string;
  merchantReference: string;
}

export interface Payment {
  convertedCurrency: any;
  convertedAmount: any;
  id: number;
  businessId: string;
  business: any;
  paymentLinkId: number;
  amount: number;
  currency: string;
  paymentMethods: PaymentMethod[];
  defaultPaymentMethod: PaymentMethod;
  customer: any;
  redirectUrl: string;
  customUrl: string;
  successMessage: string;
  settlementDestination: "bank_account" | "wallet";
  feeBearer: "business" | "customer";
  reference: string;
  merchantReference: string;
  isDisabled: boolean;
  metadata?: Record<string, any>;
  status: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaymentProviderState {
  stage: PaymentStage;
  payment: Payment;
  method: string;
  charge: Charge;
  card: Card;
  bank: Bank;
  payAttitude: PayAttitude;
  mobileMoney: MobileMoney;
  error?: string;
  business?: any;
  loading?: boolean;
  responseMessage?: string;
}

export type PaymentContextValue = {
  state: PaymentProviderState;
  setState: Dispatch<SetStateAction<Partial<PaymentProviderState>>>;
  onInputChange: Function;
  setStage: Function;
  setError: Function;
  setBusiness: Function;
  setLoading: Function;
  clearCard: Function;
  setPaymentReference: Function;
  setResponseMessage: Function;
};

const initialState: PaymentProviderState = {
  stage: null,
  method: null,
  payment: null,
  card: {
    cvv: "",
    cardNumber: "",
    expiry: "",
    otp: "",
    pin: "",
    addressLine1: "",
    addressLine2: "",
    state: "",
    country: "",
    city: "",
    zipCode: "",
  },
  bank: {
    accountNumber: null,
    bankName: null,
  },
  payAttitude: {
    phoneNumber: "",
  },
  mobileMoney: {
    operator: "",
    mobileNumber: "",
  },
  charge: null,
  error: null,
  business: null,
  loading: true,
  responseMessage: "",
};

const pusherKey = process.env.REACT_APP_PUSHER_KEY;
const pusher = pusherKey
  ? new Pusher(pusherKey, {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER,
    })
  : null;

export const PaymentProvider = ({ children }) => {
  const [state, setState] = useState<PaymentProviderState>(initialState);

  const persistedSetState = (data: Partial<PaymentProviderState>) => {
    setState((oldState) => {
      return { ...oldState, ...data };
    });
  };

  const onInputChange = (e) => {
    setState((state) => ({
      ...state,
      card: {
        ...state.card,
        [e.target.name]: e.target.value,
      },
    }));
  };

  const setStage = (stage: PaymentStage) => {
    persistedSetState({ stage });
  };

  const setError = (error: string) => {
    persistedSetState({ error });
  };

  const clearCard = () => {
    persistedSetState({ card: initialState.card });
  };

  const setBusiness = (business: any) => {
    persistedSetState({ business });
  };

  const setLoading = (loading: any) => {
    persistedSetState({ loading });
  };

  const setPaymentReference = (paymentReference: string) => {
    persistedSetState({
      payment: { ...initialState.payment, reference: paymentReference },
    });
  };

  const setResponseMessage = (message: string) => {
    persistedSetState({ responseMessage: message });
  };

  useEffect(() => {
    if (
      pusher &&
      state.payment?.reference &&
      state.charge?.status !== PaymentStatus.success
    ) {
      const channel = pusher.subscribe(`payment-${state.payment?.reference}`);
      channel.bind("success", function (data) {
        setState({
          ...state,
          payment: { ...state.payment, ...data },
        });
        setStage(PaymentStage.success);
      });

      channel.bind("failed", function (data) {
        setState({
          ...state,
          payment: { ...state.payment, ...data },
        });
        setStage(PaymentStage.failed);
      });

      return () => {
        channel.unbind();
        pusher?.unsubscribe(`payment-${state.payment?.reference}`);
      };
    }
  }, [state.payment]);

  useEffect(() => {
    if (state.charge) {
      if (state.charge.status === PaymentStatus.success) {
        setStage(PaymentStage.success);
      } else if (state.charge.status === PaymentStatus.failed) {
        setStage(PaymentStage.failed);
      }
    }
  }, [state.charge]);

  useEffect(() => {
    const paymentState = LocalStorage.getItem("payment_state");
    if (
      paymentState &&
      "/pay/" + paymentState?.payment?.reference === window.location.pathname
    ) {
      persistedSetState({ error: "" });
    }
  }, []);

  useEffect(() => {
    LocalStorage.setItem("payment_state", state);
  }, [state]);

  return (
    <PaymentContext.Provider
      value={{
        state,
        setState: persistedSetState,
        onInputChange,
        setStage,
        setError,
        setBusiness,
        setLoading,
        clearCard,
        setPaymentReference,
        setResponseMessage,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
};
